import React from "react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { EntityRelationMetadata } from "../EntityRelationsManager";
import { BUSINESS_AREA_ENTITY_TYPE } from "../BusinessAreaAddon/BusinessAreaAddon";
import { ROLE_ENTITY_TYPE } from "../RoleAddon/RoleAddon";
import { AddonParams } from "../../base/BaseAddon";
import {
  StepChangeEvent,
  StepType,
  ValidationError,
  ValidationErrorCode,
  ValidationResult,
  ValidationWarningCode
} from "../../../types/types";
import { TOASTER_TOAST_TIME } from "../../../../../../core/toaster/Toaster_model";
import toastService from "../../../../../../core/toaster/ToasterService";
import { observable } from "mobx";
import OrganisationsApi, {
  OrganisationsApi as IOrganisationsApi
} from "../../../../../../services/api/v2/organisations/Organisations.api";
import {
  CommonColDefFieldNamesEnum,
  StakeholderColDefFieldNamesEnum
} from "../../../../../../core/grids/builder/enums/AgGridColDefFieldNameEnum";
import { StakeholderAddonView } from "./StakeholderAddon_view";
import { EntityRelationAddon } from "../EntityRelationAddon";
import { NoDataFound } from "../../../components/NotFound/NoDataFound";
import RolesApi, { RolesApi as IRolesApi } from "../../../../../../services/api/v2/roles/Roles.api";
import StakeholdersApi, {
  StakeholdersApi as IStakeholdersApi
} from "../../../../../../services/api/v2/stakeholders/Stakeholders.api";

export interface UploaderStakeholderEntity extends FP.Entities.IStakeholder {
  isNew: boolean;
  isMatched: boolean;
  matchedId: number;
}

export const STAKEHOLDER_ENTITY_TYPE = "stakeholder";

export const stakeholderMetadata: EntityRelationMetadata = {
  entityType: STAKEHOLDER_ENTITY_TYPE,
  displayName: "Stakeholders",
  dependencies: [BUSINESS_AREA_ENTITY_TYPE, ROLE_ENTITY_TYPE],
  requiredForEntities: []
};

export interface Stakeholder {
  id?: string | number;
  firstName: string;
  lastName: string;
  email: string;
  businessAreaId?: string | number;
  roleId?: string | number;
  businessArea?: string;
  role?: string;
  owner: any;
  isNew?: boolean;
  isMatched?: boolean;
  matchedId?: string | number;
}

export class StakeholderAddon extends EntityRelationAddon {
  @observable data: FP.Entities.IStakeholder[];
  organisationProvider: IOrganisationsApi;
  stakeholderProvider: IStakeholdersApi;
  roleProvider: IRolesApi;
  organisationId: number;
  onBack: any;
  conflictFields: string[];

  constructor(onNext: (event: StepChangeEvent) => void, onBack: () => void, params: AddonParams) {
    super("StakeholderAddon", STAKEHOLDER_ENTITY_TYPE, stakeholderMetadata, onNext, onBack, params);
    this.organisationProvider = OrganisationsApi;
    this.stakeholderProvider = StakeholdersApi;
    this.roleProvider = RolesApi;
    this.organisationId = params.organisationId;
    this.onBack = onBack;
    this.conflictFields = [StakeholderColDefFieldNamesEnum.Email];
  }

  async getExistingData(): Promise<FP.Entities.IStakeholder[]> {
    try {
      const res = await this.stakeholderProvider.getGridData(this.organisationId);

      return res.payload || [];
    } catch (error) {
      console.error("Error fetching existing stakeholders:", error);
      return [];
    }
  }

  async getExistingEntities(): Promise<FP.Entities.IStakeholder[]> {
    return this.getExistingData();
  }

  async getExistingRelationData(): Promise<Record<string, any[]>> {
    try {
      const [businessAreas, roles, users] = await Promise.all([
        this.organisationProvider.getBusinessAreas(this.organisationId),
        this.roleProvider.getByOrganisationId(this.organisationId),
        this.organisationProvider.getUsers(this.organisationId)
      ]);

      return {
        businessArea: businessAreas.payload || [],
        role: roles.payload || [],
        owner: users.payload || []
      };
    } catch (error) {
      console.error("Error fetching relation data for stakeholders:", error);
      return {
        businessArea: [],
        role: [],
        owner: []
      };
    }
  }

  async mapEntities(
    uploadedData: any[],
    existingEntities: Partial<UploaderStakeholderEntity>[]
  ): Promise<Partial<UploaderStakeholderEntity>[]> {
    const relationData = await this.getExistingRelationData();
    const businessAreas = relationData.businessArea || [];
    const roles = relationData.role || [];
    const owners = relationData.owner || [];

    const extractedStakeholders = this.extractStakeholdersFromData(uploadedData);
    return extractedStakeholders.map(stakeholder => {
      const existingStakeholder = existingEntities.find(
        existing => existing.email?.toLowerCase() === stakeholder.email?.toLowerCase()
      );

      let mappedStakeholder = { ...stakeholder };

      if (stakeholder.businessArea && typeof stakeholder.businessArea === "string") {
        const matchedBusinessArea = businessAreas.find(
          (ba: any) =>
            ba.name &&
            String(ba.name as unknown).toLowerCase() === (stakeholder.businessArea as unknown as string).toLowerCase()
        );
        if (matchedBusinessArea) {
          mappedStakeholder = {
            ...mappedStakeholder,
            businessArea: matchedBusinessArea
          };
        }
      }

      if (stakeholder.role && typeof stakeholder.role === "string") {
        const matchedRole = roles.find(
          (role: any) =>
            role.name &&
            String(role.name as unknown).toLowerCase() === (stakeholder.role as unknown as string).toLowerCase()
        );
        if (matchedRole) {
          mappedStakeholder = {
            ...mappedStakeholder,
            role: matchedRole
          };
        }
      }

      if (stakeholder.owner && typeof stakeholder.owner === "string") {
        const matchedOwner = owners.find((owner: any) => {
          if (owner.firstName && owner.lastName) {
            const ownerFullName = `${String(owner.firstName)} ${String(owner.lastName)}`.toLowerCase();
            return ownerFullName === (stakeholder.owner as unknown as string).toLowerCase();
          }
          return false;
        });
        if (matchedOwner) {
          mappedStakeholder = {
            ...mappedStakeholder,
            owner: matchedOwner
          };
        }
      }

      if (existingStakeholder) {
        return {
          ...existingStakeholder,
          ...mappedStakeholder,
          isNew: false,
          isMatched: true,
          matchedId: existingStakeholder.id
        };
      }

      return {
        ...mappedStakeholder,
        isNew: true,
        isMatched: false
      };
    });
  }

  extractStakeholdersFromData(data: any[]): Partial<UploaderStakeholderEntity>[] {
    const stakeholders: Partial<UploaderStakeholderEntity>[] = [];

    data.forEach(row => {
      const firstName =
        row[StakeholderColDefFieldNamesEnum.FirstName] || row.firstName || row.first_name || row.FirstName;
      const lastName = row[StakeholderColDefFieldNamesEnum.LastName] || row.lastName || row.last_name || row.LastName;
      const email = row[StakeholderColDefFieldNamesEnum.Email] || row.email || row.Email;
      const businessArea =
        row[StakeholderColDefFieldNamesEnum.BusinessArea] || row.businessArea || row.business_area || row.BusinessArea;
      const role = row[StakeholderColDefFieldNamesEnum.Role] || row.role || row.Role;
      const owner = row[CommonColDefFieldNamesEnum.Owner] || row.owner || row.owner;

      if (firstName && lastName && email) {
        stakeholders.push({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          businessArea: businessArea?.trim(),
          role: role?.trim(),
          owner: owner?.trim()
        });
      }
    });

    return stakeholders;
  }

  validateEntities(mappedEntities: UploaderStakeholderEntity[]): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: ValidationError[] = [];

    mappedEntities.forEach(entity => {
      if (!entity.firstName || entity.firstName.trim() === "") {
        errors.push({
          message: `First name is required for stakeholder`,
          code: ValidationErrorCode.MISSING_NAME,
          entity
        });
      }

      if (!entity.lastName || entity.lastName.trim() === "") {
        errors.push({
          message: `Last name is required for stakeholder`,
          code: ValidationErrorCode.MISSING_NAME,
          entity
        });
      }

      if (!entity.email || entity.email.trim() === "") {
        errors.push({
          message: `Email is required for stakeholder`,
          code: ValidationErrorCode.MISSING_EMAIL,
          entity
        });
      } else if (!this.isValidEmail(entity.email)) {
        errors.push({
          message: `Invalid email format: ${entity.email}`,
          code: ValidationErrorCode.INVALID_EMAIL,
          entity
        });
      }

      if (entity.isNew) {
        warnings.push({
          message: `New stakeholder will be created: ${entity.firstName} ${entity.lastName}`,
          code: ValidationWarningCode.NEW_ENTITY,
          entity
        });
      }
    });

    const emailSet = new Set<string>();
    mappedEntities.forEach(entity => {
      if (entity.email) {
        const email = entity.email.toLowerCase();
        if (emailSet.has(email)) {
          warnings.push({
            message: `Duplicate email found: ${entity.email}`,
            code: ValidationWarningCode.DUPLICATE_ENTITY,
            entity
          });
        }
        emailSet.add(email);
      }
    });

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  validateMappingResult(result: UploaderStakeholderEntity[]): ValidationResult {
    return this.validateEntities(result);
  }

  protected getJSXElement(step: StepType, state: any): React.ReactElement {
    if (!state.data) {
      return <NoDataFound onBack={this.onBack}></NoDataFound>;
    }

    return (
      <StakeholderAddonView
        uploadedData={state.uploadedData || state.data || []}
        processedEntities={state.processedEntities || {}}
        onComplete={this.handleComplete}
      />
    );
  }

  private handleComplete = (stakeholders: UploaderStakeholderEntity[]) => {
    const validationResult = this.validateEntities(stakeholders);

    if (!validationResult.valid) {
      toastService
        .showErrorToast(TOASTER_TOAST_TIME.NORMAL, "right")
        .setContent(<span>{validationResult.errors[0].message}</span>);
      return;
    }

    if (validationResult.warnings.length > 0) {
    }

    if (this.onNext) {
      this.onNext({
        type: StepType.entityRelationValidation,
        data: stakeholders
      });
    }
  };
}
